import { computerUseTools } from './computer-use'
import { INTERNAL_TOOLS } from './internal'
import { MEMORY_TOOL_NAMES, MEMORY_TOOLS } from './memory'
import { MEMORY_UPLOAD_TOOLS } from './memory-upload'
import { OS_CATALOG_PUBLISH_TOOLS } from './os-catalog-publish'
import { OS_CEO_TOOLS } from './os-ceo'
import { OS_CHAT_FILE_TOOLS, OS_CHAT_STEP_TOOLS, OS_CHAT_TOOLS } from './os-chat'
import { OS_CHAT_THREAD_TOOLS } from './os-chat-threads'
import { OS_CONTEXT_TOOLS } from './os-context'
import { OS_ESCALATION_TOOLS } from './os-escalation'
import { OS_EXECUTION_TOOLS } from './os-execution'
import { OS_EXECUTION_STOP_TOOLS } from './os-execution-stop'
import { OS_GATE_TOOLS } from './os-gate'
import { KPI_READING_TOOLS, OS_KPI_TOOLS, ROLE_KPI_TOOLS } from './os-kpi'
import { KPI_TARGET_TOOLS } from './os-kpis'
import { OS_MAIL_TOOLS, OS_MAIL_TRIAGE_TOOLS } from './os-mail'
import { OS_MAIL_CHASE_TOOLS } from './os-mail-chase'
import { OS_MY_MAIL_TOOLS } from './os-my-mail'
import { OS_PLANNING_TOOLS } from './os-planning'
import { OS_RECOMMENDATION_TOOLS } from './os-recommendations'
import { OS_ROLE_TOOLS } from './os-roles'
import { OS_RUN_TOOLS } from './os-runs'
import { OS_SENSE_TOOLS } from './os-sense'
import { OS_TASK_ACTIVATION_TOOLS } from './os-task-activation'
import { OS_TASK_DEFINITION_TOOLS } from './os-task-definitions'
import { OS_TASK_WRITE_TOOLS } from './os-task-writes'
import { OS_TEAM_TOOLS } from './os-teams'
import { OS_WORK_TOOLS } from './os-work'
import type { BuiltInTool, BuiltInToolFamily } from './types'
import { WEB_TOOL_NAMES, WEB_TOOLS } from './web'
import { WORKSPACE_TOOL_NAMES, WORKSPACE_TOOLS } from './workspace'

/**
 * Who answers a family's calls. `runner`, `memory`, `OS` and `in-process` are also the words the runtime adapter
 * prints beside a task's tools; a Computer-Use call is answered by the run's browser.
 */
export type BuiltInToolAnswerer = 'runner' | 'memory' | 'OS' | 'browser' | 'in-process'

export const BUILT_IN_FAMILIES: Readonly<
    Record<BuiltInToolFamily, { title: string; answeredBy: BuiltInToolAnswerer }>
> = {
    workspace: { title: 'Workspace', answeredBy: 'runner' },
    memory: { title: 'Memory', answeredBy: 'memory' },
    'os.execution': { title: 'Execution labels', answeredBy: 'OS' },
    'os.execution_stop': { title: 'Stopping the execution', answeredBy: 'OS' },
    'os.gate': { title: 'Gate summary', answeredBy: 'OS' },
    'os.ceo': { title: 'CEO questions', answeredBy: 'OS' },
    'os.recommendations': { title: 'Self-improvement', answeredBy: 'OS' },
    'os.mail': { title: 'Mail', answeredBy: 'OS' },
    'os.mail_triage': { title: 'Mail triage', answeredBy: 'OS' },
    'os.mail_chase': { title: 'Mail follow-ups', answeredBy: 'OS' },
    'os.my_mail': { title: 'Your mail', answeredBy: 'OS' },
    'os.escalation': { title: 'Escalations', answeredBy: 'OS' },
    'os.kpi': { title: 'KPIs', answeredBy: 'OS' },
    'os.context': { title: 'Context', answeredBy: 'OS' },
    'os.sense': { title: 'Company state', answeredBy: 'OS' },
    'os.runs': { title: 'Unblocking runs', answeredBy: 'OS' },
    'os.chat': { title: 'Chat', answeredBy: 'OS' },
    'os.chat_threads': { title: 'Chat threads', answeredBy: 'OS' },
    'os.planning': { title: 'Planning', answeredBy: 'OS' },
    'os.org': { title: 'Organisation', answeredBy: 'OS' },
    'os.work': { title: 'Work items', answeredBy: 'OS' },
    'computer-use': { title: 'Computer use', answeredBy: 'browser' },
    web: { title: 'Web', answeredBy: 'in-process' },
    internal: { title: 'Internal', answeredBy: 'in-process' },
}

/** The run-scoped endpoint an `os.*` tool with no endpoint of its own is answered at (issue #331). */
export function runSelfToolPath(name: string): string {
    return `/v1/runs/self/tools/${name}`
}

/**
 * The `os.*` tools a run calls that the harness answers itself, never the OS MCP server: the question to the CEO is
 * the harness's own gate, answered by the decision (issue #331). Mail escalation uses the same terminal-gate
 * contract so approval can hand the CEO's instruction and exact mail to a successor without opening a second gate.
 */
export const HARNESS_ANSWERED_OS_TOOLS: readonly string[] = ['os.ask_ceo', 'os.escalate_mail']

/**
 * Whether a run's call to `tool` is answered by the OS MCP server (issue #331): an `os.*` tool a run can call, in a
 * family the OS answers, that the harness does not answer itself. The run reaches it over the built-in OS
 * connection with its own credential, never inside the run, so the entry says the MCP server serves it and where
 * its endpoint is.
 */
export function servedByOsMcp(tool: BuiltInTool): boolean {
    return (
        tool.name.startsWith('os.') &&
        tool.callers.includes('run') &&
        BUILT_IN_FAMILIES[tool.family].answeredBy === 'OS' &&
        !HARNESS_ANSWERED_OS_TOOLS.includes(tool.name)
    )
}

/**
 * One registry entry as the run's OS MCP connection serves it: an endpoint, and `api` among its callers. A tool that
 * already names an endpoint keeps it; one that does not is answered at its run-scoped tool endpoint, as the run that
 * called it. Description, schema and name are untouched, so nothing a task declares or the model reads moves.
 *
 * `mcp` is left as the entry declares it. That caller is the API-token OS MCP server's `tools/list` (#1011), which
 * can only make calls an API token may make, and a run-scoped endpoint takes a run's credential, never a token. A
 * run reaches these tools over its own built-in connection, which `servedByOsMcp` decides, not by gaining `mcp`.
 */
function withOsMcpServing(tool: BuiltInTool): BuiltInTool {
    if (!servedByOsMcp(tool)) {
        return tool
    }
    const api = tool.api ?? {
        method: 'POST' as const,
        path: runSelfToolPath(tool.name),
        scope: 'run:self',
    }
    const callers = tool.callers.includes('api') ? tool.callers : [...tool.callers, 'api' as const]
    return { ...tool, api, callers }
}

function listBuiltInTools(): readonly BuiltInTool[] {
    return listRegisteredTools().map(withOsMcpServing)
}

function listRegisteredTools(): readonly BuiltInTool[] {
    return [
        ...WORKSPACE_TOOL_NAMES.map(name => WORKSPACE_TOOLS[name]),
        ...MEMORY_TOOL_NAMES.map(name => MEMORY_TOOLS[name]),
        ...OS_EXECUTION_TOOLS,
        ...OS_EXECUTION_STOP_TOOLS,
        ...OS_GATE_TOOLS,
        ...OS_CEO_TOOLS,
        ...OS_RECOMMENDATION_TOOLS,
        ...OS_MAIL_TOOLS,
        ...OS_MAIL_TRIAGE_TOOLS,
        ...OS_MAIL_CHASE_TOOLS,
        ...OS_MY_MAIL_TOOLS,
        ...OS_ESCALATION_TOOLS,
        ...OS_KPI_TOOLS,
        ...KPI_TARGET_TOOLS,
        ...OS_CONTEXT_TOOLS,
        ...KPI_READING_TOOLS,
        ...ROLE_KPI_TOOLS,
        ...OS_SENSE_TOOLS,
        ...OS_RUN_TOOLS,
        ...OS_PLANNING_TOOLS,
        ...OS_CHAT_THREAD_TOOLS,
        ...OS_CHAT_TOOLS,
        // #1544: after every tool chat and the MCP server already listed, so each lists them last.
        ...OS_ROLE_TOOLS,
        // #1434: an enterprise's tasks as definitions, after the role tools, so each list keeps its order.
        ...OS_TASK_DEFINITION_TOOLS,
        // #1435: create a task and publish a version, then upload a team or enterprise memory note.
        // After the task-definition reads and before the team tools, so each list keeps that order.
        ...OS_TASK_WRITE_TOOLS,
        ...OS_TASK_ACTIVATION_TOOLS,
        ...MEMORY_UPLOAD_TOOLS,
        // #1545: after the role, task-definition and task-write tools, so chat and the MCP server list the team tools last.
        ...OS_TEAM_TOOLS,
        // #1735: after the team tools, so chat lists what it already offered first and this one last.
        ...OS_CHAT_FILE_TOOLS,
        // #1827: a team's work list, after every tool chat and the MCP server already listed, so each lists them last.
        ...OS_WORK_TOOLS,
        // #1844: publish a role or a team to the catalog, after every tool the MCP server already listed.
        ...OS_CATALOG_PUBLISH_TOOLS,
        // #286: the member's step list, after every tool chat already offered, so chat lists it last.
        ...OS_CHAT_STEP_TOOLS,
        ...computerUseTools(),
        ...WEB_TOOL_NAMES.map(name => WEB_TOOLS[name]),
        ...INTERNAL_TOOLS,
    ]
}

/**
 * Every built-in tool a task can call, in reference order: the one registry the model's tool specs, the harness's
 * name lists, the generated reference (`pnpm tools:reference`) and `GET /v1/tools/built-in` all read.
 */
export const BUILT_IN_TOOLS: readonly BuiltInTool[] = /* @__PURE__ */ listBuiltInTools()

let byName: ReadonlyMap<string, BuiltInTool> | undefined

/** The registered tool called exactly `name`, or undefined. A task's `#field=value` declaration is not stripped here. */
export function builtInTool(name: string): BuiltInTool | undefined {
    byName ??= new Map(BUILT_IN_TOOLS.map(tool => [tool.name, tool]))
    return byName.get(name)
}

/** The names of the registered tools `keep` accepts, in registry order. */
export function builtInToolNames(keep: (tool: BuiltInTool) => boolean): string[] {
    return BUILT_IN_TOOLS.filter(keep).map(tool => tool.name)
}

/** The tools `GET /v1/tools/built-in` serves and the reference documents: everything but test fixtures. */
export function publicBuiltInTools(): BuiltInTool[] {
    return BUILT_IN_TOOLS.filter(tool => tool.public)
}

/**
 * Whether a run answers `tool`. The save-time definition check refuses a task that declares a registered tool this
 * is false for (`tool_not_run_callable`): one only the CEO chat, the API or the MCP server offers.
 */
export function isRunCallable(tool: BuiltInTool): boolean {
    return tool.callers.includes('run')
}

/**
 * The documented tools a run can call, in registry order: what `os.list_built_in_tools` offers a task-designing run.
 * Every one passes the save-time registry checks (`tool_unknown`, `tool_not_run_callable`). The few the save also
 * holds to a declared gate (`gate_required_for_tool`, `BUILT_IN_GATE_RULES` in the API's task definition check) pass
 * once that gate is declared: `os.list_built_in_tools` reports it on their row as `gate`.
 */
export function runCallableBuiltInTools(): BuiltInTool[] {
    return BUILT_IN_TOOLS.filter(tool => tool.public && isRunCallable(tool))
}

/**
 * The namespaces the OS owns outright. Unlike a vendor's, whose catalog arrives over MCP, their whole surface is
 * registered here, so a name in one that is not registered answers to nothing.
 */
export const BUILT_IN_NAMESPACES = ['os', 'workspace', 'memory', 'web'] as const

/** Whether `name` is `os.…`, `workspace.…`, `memory.…` or `web.…`. */
export function inBuiltInNamespace(name: string): boolean {
    const dot = name.indexOf('.')
    return dot > 0 && (BUILT_IN_NAMESPACES as readonly string[]).includes(name.slice(0, dot))
}

/**
 * Who answers a call to `tool`, read from the namespace its registered tools share (`workspace.` → the runner,
 * `os.` → the OS), or undefined for a namespace the registry does not own: a vendor's, or a Computer-Use prefix,
 * which a binding chooses.
 */
export function builtInAnswerer(tool: string): BuiltInToolAnswerer | undefined {
    const dot = tool.indexOf('.')
    if (dot <= 0) {
        return undefined
    }
    const namespace = tool.slice(0, dot + 1)
    const owner = BUILT_IN_TOOLS.find(entry => entry.family !== 'computer-use' && entry.name.startsWith(namespace))
    return owner ? BUILT_IN_FAMILIES[owner.family].answeredBy : undefined
}

/**
 * The hint each registered error carries, by its reason code without any `:<detail>` suffix: the harness appends
 * it to the tool error the model reads.
 */
export function builtInErrorHints(): Record<string, string> {
    const hints: Record<string, string> = {}
    for (const tool of BUILT_IN_TOOLS) {
        for (const error of tool.errors) {
            if (error.hint) {
                hints[error.code.split(':')[0] ?? error.code] = error.hint
            }
        }
    }
    return hints
}
